import { useState, useEffect, useMemo } from "react";
import { API } from "../api";
import PageLoader from "../components/PageLoader";
import ProductCard from "../components/boutique/ProductCard";
import CategoryFilter from "../components/boutique/CategoryFilter";
import "../styles/style_boutique.css";

export default function Boutique() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadProducts() {
      try {
        // Charger les produits depuis l'API
        const result = await API.get("dataSet?p:dataSet=boutique");
        if (!cancelled) {
          setProducts(result.products || []);
          setCategories(result.categories || []);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          // Données de démonstration en cas d'erreur API
          setProducts(getDemoProducts());
          setCategories(getDemoCategories());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadProducts();
    return () => { cancelled = true; };
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesCategory = selectedCategory === "all" || product.categorie === selectedCategory;
      const matchesSearch = product.libelle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchTerm]);

  if (loading) return <PageLoader label="Chargement de la boutique" />;
  if (error) return <div className="boutique-error">Erreur : {error}</div>;

  return (
    <div className="boutique-page">
      {/* Header décoratif */}
      <div className="boutique-header">
        <div className="boutique-header-overlay"></div>
        <div className="boutique-header-content">
          <div className="floral-decoration left">
            <ion-icon name="flower-outline"></ion-icon>
          </div>
          <h1 className="boutique-title">Notre Boutique</h1>
          <p className="boutique-subtitle">Découvrez nos créations florales artisanales</p>
          <div className="floral-decoration right">
            <ion-icon name="flower-outline"></ion-icon>
          </div>
        </div>
        <div className="floating-petals">
          <span className="petal petal-1">🌸</span>
          <span className="petal petal-2">🌺</span>
          <span className="petal petal-3">🌷</span>
          <span className="petal petal-4">💐</span>
          <span className="petal petal-5">🌹</span>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="boutique-controls">
        <div className="search-container">
          <ion-icon name="search-outline" className="search-icon"></ion-icon>
          <input
            type="text"
            placeholder="Rechercher une création..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <ion-icon name="close-outline"></ion-icon>
            </button>
          )}
        </div>
        
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
        />
      </div>

      {/* Grille de produits */}
      <div className="products-section">
        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <ion-icon name="flower-outline"></ion-icon>
            <p>Aucun produit trouvé</p>
            <span>Essayez de modifier vos critères de recherche</span>
          </div>
        ) : (
          <div className="products-grid">
            {filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>

      {/* Section info */}
      <div className="boutique-info-section">
        <div className="info-card">
          <ion-icon name="leaf-outline"></ion-icon>
          <h3>Fleurs Fraîches</h3>
          <p>Sélectionnées chaque matin auprès de nos producteurs locaux</p>
        </div>
        <div className="info-card">
          <ion-icon name="gift-outline"></ion-icon>
          <h3>Emballage Soigné</h3>
          <p>Chaque bouquet est préparé avec amour et attention</p>
        </div>
        <div className="info-card">
          <ion-icon name="bicycle-outline"></ion-icon>
          <h3>Livraison Rapide</h3>
          <p>Livraison le jour même disponible sur votre secteur</p>
        </div>
      </div>
    </div>
  );
}

// Données de démonstration
function getDemoProducts() {
  return [
    {
      id: 1,
      uuid: "demo-1",
      libelle: "Bouquet Romance",
      description: "Un magnifique bouquet de roses rouges et roses, symbole d'amour et de passion",
      categorie: "Bouquets",
      imgPrincipal: null,
      variantes: [
        { taille_code: "M", taille_libelle: "Moyen", prixHT: 35.00, stock: 10 },
        { taille_code: "L", taille_libelle: "Grand", prixHT: 50.00, stock: 8 },
        { taille_code: "XL", taille_libelle: "Très Grand", prixHT: 75.00, stock: 5 }
      ],
      tva: 20.00
    },
    {
      id: 99,
      uuid: "demo-99",
      libelle: "Bouquet Personnalisable",
      description: "Choisissez la taille et le type de bouquet (tige ou bulle) selon vos envies.",
      categorie: "Bouquets",
      imgPrincipal: null,
      variantes: [
        { taille_code: "S", taille_libelle: "Petit", prixHT: 25.00, stock: 12 },
        { taille_code: "M", taille_libelle: "Moyen", prixHT: 40.00, stock: 7 },
        { taille_code: "L", taille_libelle: "Grand", prixHT: 60.00, stock: 3 }
      ],
      bouquetTypeOptions: ["tige", "bulle"],
      bouquetTypeDefault: "bulle",
      tva: 20.00
    },
    {
      id: 2,
      uuid: "demo-2",
      libelle: "Composition Printanière",
      description: "Une explosion de couleurs avec tulipes, jonquilles et renoncules",
      categorie: "Compositions",
      imgPrincipal: null,
      variantes: [
        { taille_code: "M", taille_libelle: "Moyen", prixHT: 42.00, stock: 6 },
        { taille_code: "L", taille_libelle: "Grand", prixHT: 58.00, stock: 4 },
        { taille_code: "XL", taille_libelle: "Très Grand", prixHT: 85.00, stock: 3 }
      ],
      tva: 20.00
    },
    {
      id: 3,
      uuid: "demo-3",
      libelle: "Orchidée Élégance",
      description: "Une orchidée Phalaenopsis en pot décoratif, pour une touche d'élégance",
      categorie: "Plantes",
      imgPrincipal: null,
      variantes: [
        { taille_code: "M", taille_libelle: "2 tiges", prixHT: 45.00, stock: 7 }
      ],
      tva: 20.00
    },
    {
      id: 4,
      uuid: "demo-4",
      libelle: "Bouquet Champêtre",
      description: "Un bouquet naturel et aérien avec des fleurs des champs séchées",
      categorie: "Bouquets",
      imgPrincipal: null,
      variantes: [
        { taille_code: "M", taille_libelle: "Moyen", prixHT: 28.00, stock: 12 },
        { taille_code: "L", taille_libelle: "Grand", prixHT: 40.00, stock: 8 }
      ],
      tva: 20.00
    },
    {
      id: 5,
      uuid: "demo-5",
      libelle: "Rose Éternelle",
      description: "Une rose stabilisée sous cloche, conserve sa beauté pendant des années",
      categorie: "Cadeaux",
      imgPrincipal: null,
      variantes: [
        { taille_code: "M", taille_libelle: "Standard", prixHT: 55.00, stock: 15 }
      ],
      tva: 20.00
    },
    {
      id: 6,
      uuid: "demo-6",
      libelle: "Couronne Funéraire",
      description: "Une couronne sobre et élégante pour rendre hommage avec dignité",
      categorie: "Deuil",
      imgPrincipal: null,
      variantes: [
        { taille_code: "M", taille_libelle: "60cm", prixHT: 80.00, stock: 5 },
        { taille_code: "L", taille_libelle: "80cm", prixHT: 120.00, stock: 3 },
        { taille_code: "XL", taille_libelle: "100cm", prixHT: 160.00, stock: 2 }
      ],
      tva: 20.00
    },
    {
      id: 7,
      uuid: "demo-7",
      libelle: "Bouquet Mariage",
      description: "Le bouquet parfait pour le plus beau jour de votre vie",
      categorie: "Mariage",
      imgPrincipal: null,
      variantes: [
        { taille_code: "M", taille_libelle: "Classique", prixHT: 90.00, stock: 4 },
        { taille_code: "L", taille_libelle: "Premium", prixHT: 140.00, stock: 3 },
        { taille_code: "XL", taille_libelle: "Luxe", prixHT: 200.00, stock: 2 }
      ],
      tva: 20.00
    },
    {
      id: 8,
      uuid: "demo-8",
      libelle: "Succulent Garden",
      description: "Une composition de succulentes variées dans un pot en terre cuite",
      categorie: "Plantes",
      imgPrincipal: null,
      variantes: [
        { taille_code: "M", taille_libelle: "Petit jardin", prixHT: 32.00, stock: 9 },
        { taille_code: "L", taille_libelle: "Grand jardin", prixHT: 48.00, stock: 6 }
      ],
      tva: 20.00
    }
  ];
}

function getDemoCategories() {
  return [
    { id: 1, libelle: "Bouquets" },
    { id: 2, libelle: "Compositions" },
    { id: 3, libelle: "Plantes" },
    { id: 4, libelle: "Cadeaux" },
    { id: 5, libelle: "Deuil" },
    { id: 6, libelle: "Mariage" }
  ];
}
